import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PORTRAITS = [
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/6eyiwtd6_thenewblack-f3ee5895-3f5f-4636-b9be-cd75fb87917b.jpg",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/g1g5sy57_thenewblack-bdf770de-1f92-4e6e-903e-73c6f7c00741%20%281%29.jpg",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/fb3vc1u1_thenewblack-3f30c80e-c170-49e4-865c-57c7806a1eef.jpg",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/f32ww4qy_thenewblack-bc2dd05d-3609-4467-b9a1-02f32a0bb7a4%20%281%29.jpg",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/6gpzbyzh_thenewblack-8cceb3a9-6d24-4f46-8bc8-5116b1766190.jpg",
];

export default function AutoCarousel({ images, fit = "cover" }) {
  const ref = useRef(null);
  const paused = useRef(false);
  const drag = useRef({ active: false, moved: false, startX: 0, startLeft: 0 });
  const base = images || PORTRAITS;
  const repeated = [];
  while (repeated.length < 12) repeated.push(...base);
  const loop = [...repeated, ...repeated];

  // Continuous auto-scroll that loops seamlessly; pauses on user interaction.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (el && !paused.current) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerDown = (e) => {
    paused.current = true;
    drag.current = { active: true, moved: false, startX: e.clientX, startLeft: ref.current.scrollLeft };
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 3) drag.current.moved = true;
    ref.current.scrollLeft = drag.current.startLeft - dx;
  };
  const endDrag = () => {
    drag.current.active = false;
    paused.current = false;
  };

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * 492, behavior: "smooth" });
  };

  return (
    <div className="relative -mx-6 md:-mx-16 mb-14 group/carousel">
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-28 z-10 bg-gradient-to-r from-[#FDFBF7] to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-28 z-10 bg-gradient-to-l from-[#FDFBF7] to-transparent" />

      <button
        type="button"
        aria-label="Previous"
        data-testid="carousel-prev"
        onClick={() => nudge(-1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-full border border-[#1A1A1A]/10 bg-[#FDFBF7]/70 backdrop-blur-sm text-[#1A1A1A] shadow-sm opacity-60 hover:opacity-100 hover:bg-[#FDFBF7] transition-opacity duration-300"
      >
        <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} />
      </button>
      <button
        type="button"
        aria-label="Next"
        data-testid="carousel-next"
        onClick={() => nudge(1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-full border border-[#1A1A1A]/10 bg-[#FDFBF7]/70 backdrop-blur-sm text-[#1A1A1A] shadow-sm opacity-60 hover:opacity-100 hover:bg-[#FDFBF7] transition-opacity duration-300"
      >
        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} />
      </button>

      <div
        ref={ref}
        data-testid="auto-carousel"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => {
          drag.current.active = false;
          paused.current = false;
        }}
        className="no-scrollbar flex gap-4 overflow-x-auto px-6 md:px-16 py-1 select-none cursor-grab active:cursor-grabbing"
      >
        {loop.map((it, i) => {
          const src = typeof it === "string" ? it : it.src;
          const pos = typeof it === "string" ? null : it.pos;
          const anchorClass = pos ? "" : fit === "contain" ? "object-bottom" : "object-center";
          return (
            <figure
              key={i}
              className="shrink-0 w-[168px] sm:w-[200px] md:w-[230px] aspect-[9/16] overflow-hidden rounded-lg bg-[#1A140F]"
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className={`w-full h-full object-cover ${anchorClass} pointer-events-none`}
                style={pos ? { objectPosition: pos } : undefined}
                draggable={false}
              />
            </figure>
          );
        })}
      </div>
    </div>
  );
}
